<script setup lang="ts">
  import { CreditCard02Icon } from '@gohighlevel/ghl-icons/24/outline'
  import { UIModal, UIModalContent, UIModalHeader, UITextMdNormal, useNotification } from '@gohighlevel/ghl-ui'

  import { useFillableFields, usePrintAdjust, useTypography } from '@/composable'
  import { useFillableFieldsV2 } from '@/composable/useFillableFieldsV2'
  import { PaymentPublicService } from '@/service/PaymentPublicService'
  import { useDocumentStore } from '@/store/document'
  import {
    DocumentStatus,
    IElement,
    IRecipient,
    PageElementOptions,
    SaveCardDetailsElementOptions,
    Tooltip,
    getSignatureElementColorStyles,
    isDefaultPaymentProviderSupportedForSaveCard,
    isPublicDefaultPaymentProviderConfigured,
    paymentProviderFromPublicDefaultApi,
  } from '@gohighlevel/ghl-proposals-common'
  import { UIPaymentWidgetsElement } from '@gohighlevel/payment-widget'
  import { PropType, computed, inject, nextTick, onMounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRoute } from 'vue-router'

  const props = defineProps({
    page: {
      type: Object as PropType<IElement<PageElementOptions>>,
      required: true,
    },
    element: {
      type: Object as PropType<IElement<SaveCardDetailsElementOptions>>,
      required: true,
    },
    pageId: {
      type: String,
      required: true,
    },
    tabIndex: {
      type: Number,
      required: true,
    },
    pageRef: {
      required: false,
      type: Object as () => HTMLElement,
    },
    parentRef: {
      required: false,
      type: Object as () => HTMLElement,
    },
    applyPrintAdjustment: {
      type: Boolean,
      required: false,
      default: false,
    },
  })

  const { t, locale } = useI18n()
  const route = useRoute()
  const paymentWidgetEnv = ref(
    process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'staging-version' ? 'staging' : process.env.NODE_ENV
  )
  const isProcessingInitiated = ref(false)
  const localParentRef = ref<HTMLElement | undefined>(props.parentRef)
  const localPageRef = ref<HTMLElement | undefined>(props.pageRef)
  const localLocale = computed(() => route.query.locale || locale.value || 'en-US')
  watch(
    () => props.parentRef,
    newVal => {
      localParentRef.value = newVal
    },
    { immediate: true }
  )
  watch(
    () => props.pageRef,
    newVal => {
      localPageRef.value = newVal
    },
    { immediate: true }
  )

  const { position: printPosition, elRef } = usePrintAdjust(props.page, props.element, localPageRef, localParentRef)
  const isPDFView = inject<boolean>('isPDFView')
  const isPreview = inject<boolean>('isPreview')
  const { toCapitalize } = useTypography()
  const store = useDocumentStore()
  const notification = useNotification()
  const isCCrecipient = computed(() => store.isCcRecipient)
  const activeRecipient = computed(() => store.activeRecipient)
  const isEditing = computed(() => store.isEditing)
  const { setActiveField } = useFillableFields()
  const isActiveTextFieldElemForRecipient = computed(
    () =>
      activeRecipient.value?.id === props.element?.component.options.recipient &&
      activeRecipient.value?.entityName === props.element?.component.options.entityName
  )

  const paymentLiveMode = computed(() => store.document?.paymentLiveMode ?? true)

  const recipient = computed(() => {
    const recipients = store.document?.recipients || []
    return (
      recipients.find(
        ({ id, entityName }) =>
          id === props.element?.component.options.recipient && entityName === props.element?.component.options.entityName
      ) || ({} as IRecipient)
    )
  })

  const isRecipientHasCompleted = computed(() => {
    return activeRecipient.value && activeRecipient.value?.hasCompleted && isActiveTextFieldElemForRecipient.value
  })

  const documentExpiryEnabled = computed(() => {
    if (store.documentExpiryEnabled) return true

    return false
  })
  const isDocumentExpired = computed(() => {
    if (store.isDocumentExpired) return true

    return false
  })

  const isDocumentDeclined = computed(() => {
    return store.document.status === DocumentStatus.DECLINED
  })

  const isElementFilledForRecipient = computed(() => {
    const field = store.recipients.find(({ id }) => id === recipient.value.id)?.fillableFields?.find(f => f.fieldId === props.element?.id)
    return Boolean(field?.text)
  })

  const isElementFilled = computed(() => isElementFilledForRecipient.value || recipient.value.hasCompleted)

  const isInternal = computed(() => store.isInternal)

  const disabled = computed(() => {
    return (
      !store.hasPrevRecipientsCompleted ||
      (!isPreview && !isActiveTextFieldElemForRecipient.value && !isRecipientHasCompleted.value) ||
      isCCrecipient.value ||
      (documentExpiryEnabled.value && isDocumentExpired.value) ||
      isDocumentDeclined.value ||
      store.hasUserTriggeredAcceptApiInFlow
    )
  })

  const recipientName = computed(() => {
    const { firstName, lastName } = recipient.value
    return toCapitalize(firstName ? `${firstName} ${lastName || ''}` : '')
  })

  const ghostStyles = computed(() => {
    if (props.element?.component.options?.isGhost) {
      const styleMap = props.element?.responsiveStyles.large as any
      const position = styleMap.position
      const scale = styleMap.scale
      const dimensions = styleMap.dimensions
      const stylesArray = []
      if (position) {
        let styleStr = position?.preferBottom ? `bottom: ${position.bottom}px;` : `top: ${position.top}px; `
        styleStr += position?.preferRight ? `right: ${position.right}px;` : `left: ${position.left}px;`
        styleStr += 'z-index: 100;'
        stylesArray.push(styleStr)
      }
      if (scale) {
        stylesArray.push(`transform: scale(${scale.scaleX}, ${scale.scaleY}) translate3d(0,0,0);`)
      }
      if (dimensions) {
        stylesArray.push(`height: ${dimensions.height}px; width: ${dimensions.width}px;`)
      }
      stylesArray.push('position: absolute;  display: flex;')
      return stylesArray.join(' ').trim()
    }
    return ''
  })
  const { addElementRef } = useFillableFieldsV2()

  onMounted(() => {
    if (store.isAlreadyAccepted) {
      store.updateFillableElement(props.element?.id, props.pageId, props.element?.component.options.text as string, true)
    }
    // Wait for next tick to ensure elRef is available
    nextTick(() => {
      if (elRef.value) {
        addElementRef(props.element?.id as string, elRef.value)
      }
    })
  })

  const styles = computed(() => getSignatureElementColorStyles(recipient.value))

  const colorStyles = computed(() => {
    const dimensionsObj = props.element.responsiveStyles.large.dimensions
    return {
      border: `1px solid ${styles.value.borderColor} !important`,
      height: `${dimensionsObj?.height as number}px`,
      backgroundColor: styles.value.backgroundColor,
      color: styles.value.placeholderColor,
      ...(disabled.value
        ? {
            border: '1px solid rgb(224, 224, 230)',
            backgroundColor: 'rgb(241 241 241)',
            color: 'rgba(194, 194, 194, 1)',
          }
        : {}),
      ...(isRecipientHasCompleted.value || store.hasUserTriggeredAcceptApiInFlow || isElementFilled.value
        ? {
            border: `1px solid #eaecf0 !important`,
            backgroundColor: 'transparent',
            color: 'var(--n-text-color)',
          }
        : {}),
    }
  })

  const saveCardProviderValidating = ref(false)

  const onClick = async () => {
    if (disabled.value || store.hasUserTriggeredAcceptApiInFlow) return
    if (saveCardProviderValidating.value) return

    const locationId = store.document?.locationId
    if (!locationId) {
      notification.error({
        title: t('validations.paymentProvider.required'),
        duration: 8000,
      })
      return
    }

    saveCardProviderValidating.value = true
    try {
      const { data } = await PaymentPublicService.fetchPublicDefaultPaymentProvider({
        altId: locationId,
        liveMode: paymentLiveMode.value,
      })
      const defaultProvider = paymentProviderFromPublicDefaultApi(data)
      if (!defaultProvider || !isPublicDefaultPaymentProviderConfigured(defaultProvider, paymentLiveMode.value)) {
        notification.error({
          title: t('validations.paymentProvider.unsupportedSaveCardTitle'),
          duration: 8000,
        })
        return
      }
      if (!isDefaultPaymentProviderSupportedForSaveCard(defaultProvider)) {
        notification.error({
          title: t('validations.paymentProvider.unsupportedSaveCardTitle'),
          duration: 8000,
        })
        return
      }
    } catch {
      notification.error({
        title: t('common.error'),
        duration: 8000,
      })
      return
    } finally {
      saveCardProviderValidating.value = false
    }

    if (
      !isEditing.value &&
      isActiveTextFieldElemForRecipient.value &&
      !activeRecipient.value?.hasCompleted &&
      isCCrecipient.value === false
    ) {
      store.setIsEditing(true)
    }
    openModal()
    setActiveField(props.element?.id as string)
  }
  const show = ref(false)
  const onPaymentCallback = (event: any) => {
    if (event.type === 'processing') {
      isProcessingInitiated.value = true
    } else if (event.type === 'success') {
      store.updateFillableElement(props.element?.id, props.pageId, '**** Exp. xx/xx' as string, true)
      store.updateFillableFieldForRecipient(
        recipient.value.id,
        '**** Exp. xx/xx' as string,
        props.element?.id as string,
        new Date().toISOString()
      )
      notification.success({
        title: t('documentViewer.cardSecurelySavedOnFile'),
        description: t('documentViewer.cardSecurelySavedOnFileDescription'),
        duration: 5000,
      })
      closeModal()
    } else if (event.type === 'error') {
      //Silently handle error if processing is not initiated, thrown by payment widget
      if (event.message === '') return
      notification.error({
        title: t('common.error'),
        description: event.message || t('documentViewer.anErrorOccurredWhileSavingCardDetails'),
        duration: 10000,
      })
    }
  }

  const cancelCallback = () => {
    show.value = false
  }

  const openModal = () => {
    show.value = true
  }
  const closeModal = () => {
    show.value = false
  }
</script>

<template>
  <UIModal
    v-model:show="show"
    id="save-card-details-modal"
    :key="'save-card-details-modal' + props.element.id"
    :width="800"
    class-name="save-card-details-modal page:!w-screen page:h-screen page:!fixed page:!top-0 page:!left-0 page:!z-[500] page:pt-0 page:px-4 page:pb-4 page:rounded-none"
  >
    <template #header>
      <UIModalHeader id="modal-header" type="success" :title="$t('documentViewer.saveCardDetails')" @close="cancelCallback" />
    </template>

    <UIModalContent class="save-card-details-modal-content">
      <UIPaymentWidgetsElement
        ref="paymentWidgetRef"
        v-if="store.document.locationId"
        :env="paymentWidgetEnv"
        id="charge-payment-element"
        :alt-id="store.document.locationId"
        alt-type="location"
        :live-mode="paymentLiveMode"
        @payment-callback="onPaymentCallback"
        @cancel-callback="cancelCallback"
        :action="'ADD_CARD_ON_FILE'"
        :contact-id="recipient.id"
        :use-translations="true"
        :show-mode-badge="false"
        :debug="true"
        :source="'document_contracts'"
        :close-header-action="true"
        :cancel-footer-action="true"
        :hide-live-mode-toggle="!isInternal"
        :footerText="t('documentViewer.saveCardConsentMessage')"
        :language="localLocale"
      />
    </UIModalContent>
  </UIModal>

  <div
    ref="elRef"
    class="save-card-details-field-el"
    :style="ghostStyles"
    :id="props.element?.id"
    :class="{
      'cursor-not-allowed': disabled && isPreview,
      'cursor-pointer': !isRecipientHasCompleted && !disabled && !isPreview,
    }"
    @click="!isRecipientHasCompleted && !disabled && !isPreview ? onClick() : null"
  >
    <Tooltip direction="bottom" :enable="true">
      <template #trigger>
        <div class="print:break-inside-avoid !break-inside-avoid box-border date-field-el-wrapper">
          <span v-if="element?.component.options.required && !disabled && !isPDFView && !isRecipientHasCompleted" class="option--required">
            {{ $t('common.requiredIndicator') }}
          </span>
          <div class="el-wrapper collect-documents-el relative" style="height: inherit; width: inherit">
            <div :style="colorStyles" class="save-card-details-inner rounded-sm flex flex-col justify-center">
              <div class="flex items-center gap-2 px-2">
                <CreditCard02Icon class="h-5 w-5 shrink-0" aria-hidden="true" />
                <UITextMdNormal class="save-card-details-label min-w-0 flex-1 py-2 text-ellipsis">
                  {{ props.element?.component.options.text || props.element?.component.options.placeholder }}
                </UITextMdNormal>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #content>
        <UITextSmRegular class="capitalize font-semibold px-2 py-1 text-xs" :style="{ color: styles.placeholderColor }">
          {{ recipientName }}
        </UITextSmRegular>
      </template>
    </Tooltip>
  </div>
</template>

<style lang="scss">
  /* Reduce top spacing: payment widget already has its own top margin */
  .save-card-details-modal-content {
    padding-top: 0 !important;
    margin-top: 0 !important;
  }

  .option--required {
    position: absolute;
    right: -7px;
    top: -17px;
    color: red;
    font-size: 22px;
    font-weight: 700;
  }

  @media print {
    .save-card-details-field-el {
      break-inside: avoid-page !important;
      &.apply-print-adjustment {
        transform: v-bind('printPosition.top') !important;
      }
    }
  }

  .text-ellipsis {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  /* Stable row height when text + placeholder are empty (preview/PDF); flex avoids `0fr` collapsing the icon column */
  .save-card-details-label {
    min-height: 1.25em;
  }
</style>
